'use strict';

// Import Swiper core and modules
import Swiper from "swiper";
import {Autoplay, Navigation, Pagination} from "swiper/modules";

// Import Swiper styles
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

const Slider = {
    els: null,
    instances: new Map(), // Store instances using a Map

    init: function () {
        Slider.els = document.querySelectorAll(".c-slider");

        if (Slider.els.length > 0) {
            Slider.els.forEach(el => {
                Slider.create(el);
            });
        }

        // Handle window resize
        window.addEventListener("resize", Slider.handleResize);

        // Cleanup before printing
        window.addEventListener("beforeprint", () => {
            Slider.instances.forEach(({swiperInstance, el}) => {
                if (swiperInstance) {
                    swiperInstance.destroy(true, true);
                    Slider.instances.set(el, {swiperInstance: null, el});
                }
            });
        });
    },

    create: function (el) {
        const sliderEl = el.querySelector(".swiper");
        if (!sliderEl) {
            console.error("Swiper container (.swiper) not found in:", el);
            return;
        }

        // Check if Swiper is already initialized
        const existingInstance = Slider.instances.get(el);
        if (existingInstance && existingInstance.swiperInstance) {
            console.log("Swiper already initialized for:", el);
            return;
        }

        // Store element even if Swiper isn't created yet
        if (!Slider.instances.has(el)) {
            Slider.instances.set(el, {swiperInstance: null, el});
        }

        // Prevent initialization on large screens if `data-mobile` is true
        if (window.matchMedia("(min-width: 1025px)").matches && sliderEl.dataset.mobile === "true") {
            return;
        }

        const navigationPrev = el.querySelector(".c-slider__navigation-prev");
        const navigationNext = el.querySelector(".c-slider__navigation-next");
        const pagination = el.querySelector(".swiper-pagination");

        const mobileSlides = sliderEl.dataset.mobileSlide === 'auto' ? 'auto' : parseInt(sliderEl.dataset.mobileSlide || 1);
        const mobileSlidesPerGroup = parseInt(sliderEl.dataset.mobileSlidePerGroup || 1);
        const desktopSlides = sliderEl.dataset.slide === 'auto' ? 'auto' : parseInt(sliderEl.dataset.slide || 1);
        const desktopSlidesPerGroup = parseInt(sliderEl.dataset.slidePerGroup || 1);
        const desktopOffset = parseInt(sliderEl.dataset.offsetAfter || 0);

        const hasMobilePagination = !(sliderEl.dataset.mobilePagination && sliderEl.dataset.mobilePagination === 'false');
        const hasPagination = !(sliderEl.dataset.pagination && sliderEl.dataset.pagination === 'false');
        const isEnabled = sliderEl.dataset.condition
            ? sliderEl.querySelectorAll(".swiper-slide").length > parseInt(sliderEl.dataset.condition)
            : true;

        const isPublications = sliderEl.dataset.publications;

        if (isEnabled) {
            // Create Swiper instance
            const swiperInstance = new Swiper(sliderEl, {
                modules: [Autoplay, Navigation, Pagination],
                navigation: {
                    prevEl: navigationPrev,
                    nextEl: navigationNext
                },
                pagination: {
                    el: pagination,
                    type: "bullets",
                    clickable: true,
                    enabled: hasMobilePagination
                },
                keyboard: {
                    enabled: true,
                    onlyInViewport: true,
                },
                a11y: {
                    enabled: true,
                    prevSlideMessage: "Slide précédente",
                    nextSlideMessage: "Slide suivante",
                    firstSlideMessage: "Première slide",
                    lastSlideMessage: "Dernière slide",
                    paginationBulletMessage: "Aller à la slide {{index}}",
                    slideLabelMessage: "Slide {{index}} sur {{slidesLength}}",
                    containerMessage: "Carrousel de slides"
                },
                slidesPerView: mobileSlides,
                slidesPerGroup: mobileSlidesPerGroup,
                spaceBetween: 24,
                loop: false,
                speed: 600,
                autoHeight: false,
                watchSlidesProgress: true,
                watchOverflow: true,
                breakpoints: {
                    1025: {
                        mousewheel: !isPublications,
                        preventInteractionOnTransition: !isPublications,
                        allowTouchMove: !isPublications,
                        pagination: {
                            enabled: hasPagination,
                            renderBullet: function (index, className) {
                                if (isPublications) {
                                    const totalSlides = this.slides.length;
                                    // Calculer le nombre de bullets basé sur des groupes de 4 slides
                                    // 1-4 slides = 1 bullet (pas de pagination)
                                    // 5-8 slides = 2 bullets
                                    // 9-12 slides = 3 bullets
                                    // 13-16 slides = 4 bullets
                                    const numberOfBullets = Math.ceil(totalSlides / 4);

                                    // Afficher la pagination seulement s'il y a plus d'un groupe
                                    if (numberOfBullets > 1 && index < numberOfBullets) {
                                        return `<button class="${className}"></button>`;
                                    }
                                    return '';
                                } else {
                                    return `<button class="swiper-pagination-bullet"></button>`;
                                }
                            }
                        },
                        slidesPerView: desktopSlides,
                        slidesOffsetAfter: desktopOffset,
                        slidesPerGroup: desktopSlidesPerGroup
                    },
                },
                ...(el.classList.contains('headerHero') && {
                    autoplay: { delay: 5000 }
                }),// Ajout de l'autoplay uniquement slider HP
                ...(el.classList.contains('f-images') && {
                    autoHeight: true
                })
            });

            // Store instance
            Slider.instances.set(el, {swiperInstance, el});

            // Un slider dont le rendu est ignoré par content-visibility (cf. sliderDocuments.scss)
            // n'a aucune dimension mesurable à l'initialisation : Swiper doit re-mesurer lorsque le
            // moteur décide de faire le rendu... (avec un appel à update)
            // Les sliders `data-mobile="true"` sont détruits puis recréés sur le même `el` à chaque
            // passage sous 1025px (cf. handleResize) : sans ce garde-fou, un nouveau listener
            // s'accumulerait sur `el` à chaque cycle resize/rotation.
            if ('oncontentvisibilityautostatechange' in el && !el.dataset.cvListenerBound) {
                el.dataset.cvListenerBound = 'true';
                el.addEventListener('contentvisibilityautostatechange', (event) => {
                    const current = Slider.instances.get(el);

                    if (!event.skipped && current && current.swiperInstance && !current.swiperInstance.destroyed) {
                        current.swiperInstance.update();
                    }
                });
            }

            // Add keyboard accessibility improvements
            Slider.setupKeyboardAccessibility(sliderEl, swiperInstance);

            if (isPublications) {
                if (window.matchMedia("(min-width: 1025px)").matches) {

                    const slides = sliderEl.querySelectorAll('.swiper-slide');
                    let initialTranslate;
                    let visibleSlides = [];
                    let isTransforming = false; // Flag to track if a transformation is in progress

                    // Fonction utilitaire pour snapper à la valeur la plus proche
                    function getNearestSnap(value, snapPoints) {
                        return snapPoints.reduce((closest, snap) =>
                            Math.abs(value - snap) < Math.abs(value - closest) ? snap : closest
                        );
                    }

                    slides.forEach((slide, index) => {
                        slide.addEventListener('mouseenter', () => {
                            if (isTransforming) return;

                            if (slide.classList.contains('swiper-slide-visible')) {
                                const rawTranslate = swiperInstance.getTranslate();

                                slide.classList.add('hovered');
                                visibleSlides = Array.from(sliderEl.querySelectorAll('.swiper-slide-visible'));

                                const activeIndex = swiperInstance.activeIndex;
                                const targetIndex = (activeIndex + 3) % slides.length;

                                if (index === targetIndex && slides[activeIndex + 1] && slides.length > 3) {
                                    isTransforming = true;

                                    // Calculer snapPoints logiques
                                    const snapPoints = [];
                                    const totalSlides = swiperInstance.slides.length;
                                    const slidesPerGroup = 4;

                                    for (let i = 0; i < totalSlides; i += slidesPerGroup) {
                                        const groupedSlide = swiperInstance.slides[i];
                                        if (groupedSlide) {
                                            snapPoints.push(-groupedSlide.offsetLeft);
                                        }
                                    }

// Ajouter un snap point pour le dernier groupe réel (même incomplet)
                                    if (totalSlides % slidesPerGroup !== 0) {
                                        const lastGroupStartIndex = totalSlides - slidesPerGroup;
                                        const lastGroupSlide = swiperInstance.slides[lastGroupStartIndex];
                                        if (lastGroupSlide) {
                                            const lastGroupOffset = -lastGroupSlide.offsetLeft;
                                            if (!snapPoints.includes(lastGroupOffset)) {
                                                snapPoints.push(lastGroupOffset);
                                            }
                                        }
                                    }

                                    const uniqueSnapPoints = [...new Set(snapPoints)];


                                    setTimeout(() => {
                                        initialTranslate = getNearestSnap(rawTranslate, uniqueSnapPoints);
                                    }, 50);

                                    if (initialTranslate === 0) {
                                        setTimeout(() => {
                                            const rawTranslate2 = swiperInstance.getTranslate();
                                            initialTranslate = getNearestSnap(rawTranslate2, uniqueSnapPoints);
                                        }, 50);
                                    }

                                    const targetPosition = slides[activeIndex + 1].offsetLeft;

                                    setTimeout(() => {
                                        swiperInstance.setTranslate(-targetPosition);
                                        isTransforming = false;
                                    }, 300);
                                }
                            }
                        });


                        slide.addEventListener('mouseleave', () => {
                            slide.classList.remove('hovered');
                            if(isTransforming){
                                return;
                            }
                            const isLastVisibleSlide = visibleSlides[visibleSlides.length - 1] === slide;
                            if (isLastVisibleSlide && typeof initialTranslate === 'number') {
                                setTimeout(() => {
                                    swiperInstance.setTranslate(initialTranslate);
                                    isTransforming = false;
                                }, 300);
                            }
                        });

                        const buttonsCards = slide.querySelectorAll('.c-buttonsCards a, .c-buttonsCards button');
                        buttonsCards.forEach(button => {
                            button.addEventListener('focusin', () => {
                                slide.classList.add('hovered');
                            });

                            button.addEventListener('focusout', () => {
                                const stillFocused = slide.querySelector('.c-buttonsCards a:focus, .c-buttonsCards button:focus');
                                if (!stillFocused) {
                                    slide.classList.remove('hovered');
                                }
                            });
                        });
                    });



                    const checkArrowVisibility = () => {
                        const nextArrow = sliderEl.querySelector('.swiper-button-next');
                        const prevArrow = sliderEl.querySelector('.swiper-button-prev');
                        const totalSlides = swiperInstance.slides.length;
                        const slidesPerGroup = 4; // Vous avez data-slide-per-group="4"

                        // Calculer le nombre de bullets basé sur des groupes de 4 slides
                        const numberOfBullets = Math.ceil(totalSlides / slidesPerGroup);

                        // Si moins de 2 bullets, cacher complètement les flèches
                        if (numberOfBullets < 2) {
                            nextArrow.style.display = 'none';
                            prevArrow.style.display = 'none';
                            return;
                        }

                        // Calculer la position actuelle et les limites
                        const currentIndex = swiperInstance.activeIndex;
                        const isAtStart = currentIndex === 0;
                        const isAtEnd = currentIndex + slidesPerGroup >= totalSlides;

                        // Gestion de la flèche précédente
                        if (isAtStart) {
                            prevArrow.style.display = 'none';
                        } else {
                            prevArrow.style.display = 'block';
                        }

                        // Gestion de la flèche suivante
                        if (isAtEnd) {
                            nextArrow.style.display = 'none';
                        } else {
                            nextArrow.style.display = 'block';
                        }
                    };

                    const updateBullets = () => {
                        const bullets = sliderEl.querySelectorAll('.swiper-pagination-bullet');
                        const activeSlideIndex = swiperInstance.realIndex; // Use realIndex for individual slides

                        bullets.forEach((bullet, i) => {
                            if (i === activeSlideIndex) {
                                bullet.classList.add('active'); // Highlight active bullet
                            } else {
                                bullet.classList.remove('active'); // Remove highlight from others
                            }
                        });
                    };
                    swiperInstance.on('slideChange', () => {
                        checkArrowVisibility();
                        updateBullets();
                    });
                    // Lors de l'initialisation
                    swiperInstance.on('init', checkArrowVisibility);
// Lors de la mise à jour du swiper
                    swiperInstance.on('update', checkArrowVisibility);
                    // Lors du changement de transition
                    swiperInstance.on('transitionEnd', checkArrowVisibility);
                }
            }
        }
    },
    handleResize: function () {
        Slider.instances.forEach(({swiperInstance, el}) => {
            const sliderEl = el.querySelector(".swiper");
            const isMobileOnly = sliderEl.dataset.mobile === "true";

            if (isMobileOnly) {
                if (window.matchMedia("(min-width: 1025px)").matches) {
                    // Destroy Swiper on large screens
                    if (swiperInstance) {
                        swiperInstance.destroy(true, true);
                        Slider.instances.set(el, {swiperInstance: null, el}); // Update to null
                    }
                } else if (!swiperInstance) {
                    // Recreate Swiper if it was destroyed
                    Slider.create(el);
                }
            }
        });
    },
    setupKeyboardAccessibility: function (sliderEl, swiperInstance) {
        // Get all focusable elements within the slider
        const slides = sliderEl.querySelectorAll('.swiper-slide');

        sliderEl.addEventListener('keydown', (e) => {
            if (e.key === 'ArrowRight') {
                swiperInstance.slideNext();
                e.preventDefault();

                setTimeout(() => {
                    const activeSlide = sliderEl.querySelector('.swiper-slide-active');
                    if (activeSlide) activeSlide.focus();
                }, 100);
            } else if (e.key === 'ArrowLeft') {
                swiperInstance.slidePrev();
                e.preventDefault();

                setTimeout(() => {
                    const activeSlide = sliderEl.querySelector('.swiper-slide-active');
                    if (activeSlide) activeSlide.focus();
                }, 100);
            }
        });

        // Make navigation and pagination buttons accessible
        const navigationPrev = sliderEl.parentElement.querySelector('.c-slider__navigation-prev');
        const navigationNext = sliderEl.parentElement.querySelector('.c-slider__navigation-next');
        const paginationBullets = sliderEl.parentElement.querySelectorAll('.swiper-pagination-bullet');

        if (navigationPrev) {
            navigationPrev.setAttribute('aria-label', 'Slide précédente');
        }

        if (navigationNext) {
            navigationNext.setAttribute('aria-label', 'Slide suivante');
        }

        // Add aria-labels to pagination bullets
        paginationBullets.forEach((bullet, index) => {
            bullet.setAttribute('aria-label', `Aller à la slide ${index + 1}`);
        });

        // Update ARIA attributes and tabindex on slide change
        swiperInstance.on('slideChange', updateSlidesAccessibility);
        swiperInstance.on('slidesLengthChange', updateSlidesAccessibility);
        swiperInstance.on('breakpoint', updateSlidesAccessibility);
        swiperInstance.on('resize', updateSlidesAccessibility);

        function updateSlidesAccessibility() {
            slides.forEach((slide, index) => {
                const isVisible = slide.classList.contains('swiper-slide-visible');

                if (isVisible) {
                    slide.setAttribute('aria-hidden', 'false');
                } else {
                    // Retirer tabindex des slides invisibles pour éviter la tabulation supplémentaire
                    slide.setAttribute('tabindex', '-1');
                    //slide.setAttribute('aria-hidden', 'true');
                }
            });
        }

        // Initial setup
        updateSlidesAccessibility();

        // Add appropriate role
        sliderEl.setAttribute('role', 'region');
        sliderEl.setAttribute('aria-label', 'Carrousel');
    }
};

export default Slider;